import React, { useEffect, useRef, useState } from "react";
import { useGoogleMaps } from "react-hook-google-maps";
import { GOOGLE_MAPS_KEY } from "../constants/general-constants";
import { JWT } from "../constants/local-storage";

export default function ReusableGoogleMapWithPanel({
  apiUrl,
  markerIcon,
  center,
  activeSub
}) {
  const [locations, setLocations] = useState([]);
  const [hoveredId, setHoveredId] = useState(null);

  const markersRef = useRef({});

  const { ref, map, google } = useGoogleMaps(GOOGLE_MAPS_KEY, {
    center,
    zoom: 14,
    mapTypeId: "roadmap",
    disableDefaultUI: true
  });

  /* ===============================
     LOAD DATA
     =============================== */

  useEffect(() => {
    if (!apiUrl) return;

    async function load() {
      try {
        const response = await fetch(apiUrl, {
          method: "GET",
          headers: {
            Authorization: localStorage.getItem(JWT)
          },
          credentials: "include"
        });

        const data = await response.json();
        if (!data || !Array.isArray(data.results)) {
          setLocations([]);
          return;
        }

        setLocations(
          data.results.map(r => ({
            ...r,
            lat: Number(r.latitude),
            lng: Number(r.longitude)
          }))
        );
      } catch {
        setLocations([]);
      }
    }

    load();
  }, [apiUrl]);

  /* ===============================
     MARKERS
     =============================== */

  useEffect(() => {
    if (!map || !google) return;

    Object.values(markersRef.current).forEach(m => m.setMap(null));
    markersRef.current = {};

    if (!locations.length) return;

    const bounds = new google.maps.LatLngBounds();

    locations.forEach(loc => {
      if (isNaN(loc.lat) || isNaN(loc.lng)) return;

      const position = { lat: loc.lat, lng: loc.lng };
      bounds.extend(position);

      const marker = new google.maps.Marker({
        position,
        map,
        icon: getMarkerIcon(markerIcon, false)
      });

      marker.addListener("mouseover", () => {
        setHoveredId(loc.id);
        marker.setIcon(getMarkerIcon(markerIcon, true));
      });

      marker.addListener("mouseout", () => {
        setHoveredId(null);
        marker.setIcon(getMarkerIcon(markerIcon, false));
      });

      markersRef.current[loc.id] = marker;
    });

    if (locations.length > 1) {
      map.fitBounds(bounds);
    }
  }, [map, google, locations, markerIcon]);

  /* ===============================
     CARD <-> MARKER SYNC
     =============================== */

  function highlightMarker(id) {
    const marker = markersRef.current[id];
    if (marker) marker.setIcon(getMarkerIcon(markerIcon, true));
  }

  function resetMarker(id) {
    const marker = markersRef.current[id];
    if (marker) marker.setIcon(getMarkerIcon(markerIcon, false));
  }

  /* ===============================
     UI
     =============================== */

  return (
    <div style={wrapper}>
      <div ref={ref} style={mapStyle} />

      <div
        style={{
          ...listPanel,
          transform: activeSub
            ? "translateY(35%)"   // 👈 LESS slide
            : "translateY(100%)"
        }}
      >
        <div style={listHeader}>{activeSub}</div>

        <div style={listGrid}>
          {locations.slice(0, 20).map((loc, i) => {
            const active = hoveredId === loc.id;

            return (
              <div
                key={loc.id || i}
                style={{
                  ...listCard,
                  background: active ? "#1e88e5" : "#f5f7fa",
                  color: active ? "#ffffff" : "#000000",
                  transform: active ? "scale(1.04)" : "scale(1)"
                }}
                onMouseEnter={() => {
                  setHoveredId(loc.id);
                  highlightMarker(loc.id);
                }}
                onMouseLeave={() => {
                  setHoveredId(null);
                  resetMarker(loc.id);
                }}
              >
                <div style={{
                  ...listTitle,
                  color: active ? "#ffffff" : "#000000"
                }}>
                  {loc.name || `Location ${i + 1}`}
                </div>

                <div style={{
                  ...listMeta,
                  color: active ? "rgba(255,255,255,0.85)" : "rgba(0,0,0,0.6)"
                }}>
                  {loc.distanceKm != null
                    ? loc.distanceKm.toFixed(2) + " km"
                    : "—"}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ===============================
   MARKER ICONS
   =============================== */

function getMarkerIcon(url, highlighted) {
  return {
    url,
    scaledSize: highlighted
      ? new window.google.maps.Size(36, 48)
      : new window.google.maps.Size(30, 40),
    anchor: highlighted
      ? new window.google.maps.Point(18, 48)
      : new window.google.maps.Point(15, 40)
  };
}

/* ===============================
   STYLES
   =============================== */

const wrapper = {
  position: "relative",
  width: "100%",
  height: "100%",
  borderRadius: "18px",
  overflow: "hidden",
  background: "#eaeaea"
};

const mapStyle = {
  width: "100%",
  height: "100%"
};

const listPanel = {
  position: "absolute",
  bottom: 0,
  left: 0,
  right: 0,
  height: "200px",            // 👈 shorter panel
  background: "#ffffff",
  borderTopLeftRadius: "20px",
  borderTopRightRadius: "20px",
  boxShadow: "0 -10px 30px rgba(0,0,0,0.25)",
  transition: "transform 0.45s cubic-bezier(0.4, 0.0, 0.2, 1)",
  padding: "16px",
  zIndex: 10
};

const listHeader = {
  fontSize: "16px",
  fontWeight: 800,
  marginBottom: "12px"
};

const listGrid = {
  display: "flex",
  gap: "12px",
  overflowX: "auto",
  overflowY: "hidden",
  paddingBottom: "6px"
};

const listCard = {
  minWidth: "180px",
  borderRadius: "12px",
  padding: "10px",
  boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
  transition: "all 0.2s ease",
  flexShrink: 0,
  cursor: "pointer"
};

const listTitle = {
  fontSize: "13px",
  fontWeight: 700
};

const listMeta = {
  fontSize: "11px"
};
